import { reverseGeocoding } from '../utils/geoCoding';
import { GeocodingCache } from './geocoding-cache.service';

interface QueueItem {
  coords: { lat: number; lng: number };
  resolve: (address: string) => void;
  reject: (error: any) => void;
  priority?: number;
}

export class GeocodingService {
  private static instance: GeocodingService;
  private queue: QueueItem[] = [];
  private processing = false;
  private requestCount = 0;
  private resetTime = Date.now() + 86400000; // 24h
  private readonly DAILY_LIMIT = 2500; // Google Maps free tier
  private readonly BATCH_SIZE = 5;
  private readonly REQUEST_DELAY = 200; // ms between requests
  private readonly BATCH_DELAY = 1000; // ms between batches

  private constructor() {
    // Load request count from localStorage
    this.loadRequestCount();
    // Clean invalid cache entries (errors from previous API issues)
    GeocodingCache.cleanInvalidEntries();
    // Clean imprecise cache entries (city-only addresses)
    const cleanedImprecise = GeocodingCache.cleanImpreciseEntries();
    if (cleanedImprecise > 0) {
      console.log(`[GeocodingService] Cleaned ${cleanedImprecise} imprecise cache entries`);
    }
  }

  static getInstance(): GeocodingService {
    if (!this.instance) {
      this.instance = new GeocodingService();
    }
    return this.instance;
  }

  /**
   * Get address for coordinates with caching and rate limiting
   * If a cached address is imprecise (city-only), it will be re-fetched
   */
  async getAddress(latitude: number, longitude: number, priority: number = 0): Promise<string> {
    // Validate coordinates
    if (!latitude || !longitude || isNaN(latitude) || isNaN(longitude)) {
      return 'Coordonnées invalides';
    }

    // Check cache first
    const cached = GeocodingCache.get(latitude, longitude);

    // If we have a cached address that is precise, return it immediately
    if (cached && !GeocodingCache.isAddressImprecise(latitude, longitude)) {
      return cached;
    }

    // If we have an imprecise cached address, we'll try to get a better one
    // but return the imprecise one if we can't (to avoid showing nothing)
    const hasImpreciseCached = cached && GeocodingCache.isAddressImprecise(latitude, longitude);
    if (hasImpreciseCached) {
      console.log(`[GeocodingService] Found imprecise cached address "${cached}", attempting refresh...`);
    }

    // Check if we've hit the daily limit
    if (this.requestCount >= this.DAILY_LIMIT) {
      // Return cached imprecise address if available, otherwise approximation
      return cached || this.getApproximateLocation(latitude, longitude);
    }

    // Add to queue with a flag for imprecise refresh
    return new Promise((resolve, reject) => {
      this.queue.push({
        coords: { lat: latitude, lng: longitude },
        resolve: (newAddress: string) => {
          // If the new address is better (more precise), use it
          // Otherwise fall back to the cached imprecise address
          if (newAddress && newAddress !== 'Adresse inconnue') {
            resolve(newAddress);
          } else if (hasImpreciseCached) {
            resolve(cached);
          } else {
            resolve(newAddress);
          }
        },
        reject,
        priority: hasImpreciseCached ? priority + 1 : priority // Prioritize refreshes
      });

      // Sort by priority (higher priority first)
      this.queue.sort((a, b) => (b.priority || 0) - (a.priority || 0));

      // Start processing if not already
      this.processQueue();
    });
  }

  /**
   * Process the geocoding queue with rate limiting
   */
  private async processQueue() {
    if (this.processing || this.queue.length === 0) return;

    this.processing = true;

    try {
      // Reset counter if 24h have passed
      if (Date.now() > this.resetTime) {
        this.requestCount = 0;
        this.resetTime = Date.now() + 86400000;
        this.saveRequestCount();
      }

      // Process a batch
      const batch = this.queue.splice(0, this.BATCH_SIZE);
      
      // Group by similar coordinates to reduce duplicate requests
      const uniqueRequests = this.groupSimilarCoordinates(batch);

      for (const group of uniqueRequests) {
        const { coords, items } = group;
        
        try {
          // Check cache again (might have been added by another request)
          const cached = GeocodingCache.get(coords.lat, coords.lng);
          if (cached) {
            items.forEach(item => item.resolve(cached));
            continue;
          }

          // Check daily limit
          if (this.requestCount >= this.DAILY_LIMIT) {
            const fallback = this.getApproximateLocation(coords.lat, coords.lng);
            items.forEach(item => item.resolve(fallback));
            continue;
          }

          // Make the API call
          const result = await reverseGeocoding(coords.lat, coords.lng);
          const address = this.formatAddress(result);

          // Only cache if we got a valid address (not empty)
          if (address && address !== 'Adresse inconnue') {
            GeocodingCache.set(coords.lat, coords.lng, address);
          }

          // Increment counter and save
          this.requestCount++;
          this.saveRequestCount();

          // Resolve all items in this group
          items.forEach(item => item.resolve(address));

          // Rate limiting delay
          if (this.queue.length > 0) {
            await this.delay(this.REQUEST_DELAY);
          }
        } catch (error) {
          console.error('Geocoding error:', error);
          const fallback = this.getApproximateLocation(coords.lat, coords.lng);
          items.forEach(item => item.resolve(fallback));
        }
      }
    } finally {
      this.processing = false;

      // Continue processing if queue not empty
      if (this.queue.length > 0) {
        setTimeout(() => this.processQueue(), this.BATCH_DELAY);
      }
    }
  }

  /**
   * Group similar coordinates to avoid duplicate requests
   */
  private groupSimilarCoordinates(items: QueueItem[]): Array<{
    coords: { lat: number; lng: number };
    items: QueueItem[];
  }> {
    const groups = new Map<string, { coords: { lat: number; lng: number }; items: QueueItem[] }>();

    items.forEach(item => {
      // Round to 6 decimal places for grouping
      const key = `${item.coords.lat.toFixed(6)},${item.coords.lng.toFixed(6)}`;
      
      if (!groups.has(key)) {
        groups.set(key, {
          coords: item.coords,
          items: []
        });
      }
      
      groups.get(key)!.items.push(item);
    });

    return Array.from(groups.values());
  }

  /**
   * Format address from geocoding result
   * Returns a concise address: "Street Number Street, City" without postal code
   */
  private formatAddress(result: any): string {
    if (!result) {
      return 'Adresse inconnue';
    }

    const parts: string[] = [];

    // Build street part: "Number Street"
    if (result.street) {
      if (result.streetNumber) {
        parts.push(`${result.streetNumber} ${result.street}`);
      } else {
        parts.push(result.street);
      }
    }

    // Add city
    if (result.city) {
      parts.push(result.city);
    }

    // If we have parts, join them
    if (parts.length > 0) {
      return parts.join(', ');
    }

    // Fallback: use formatted address but remove postal code
    if (result.address) {
      return this.removePostalCode(result.address);
    }

    return 'Adresse inconnue';
  }

  /**
   * Remove postal code from an address string
   */
  private removePostalCode(address: string): string {
    // Remove postal codes (5 digits for France, various formats for other countries)
    // Pattern matches: "75001", "75 001", "F-75001", etc.
    const withoutPostalCode = address
      .replace(/\b[A-Z]{0,2}-?\d{4,5}\b/g, '')  // Remove postal codes
      .replace(/\s{2,}/g, ' ')                   // Clean up multiple spaces
      .replace(/,\s*,/g, ',')                    // Clean up empty segments
      .replace(/,\s*$/g, '')                     // Remove trailing comma
      .replace(/^\s*,/g, '')                     // Remove leading comma
      .trim();

    return withoutPostalCode || address;
  }

  /**
   * Get approximate location without API call
   */
  private getApproximateLocation(lat: number, lng: number): string {
    // France regions approximation
    if (lat >= 48.5 && lat <= 49 && lng >= 2 && lng <= 2.8) {
      return 'Région Parisienne';
    } else if (lat >= 43 && lat <= 43.5 && lng >= 5 && lng <= 5.5) {
      return 'Région Marseille';
    } else if (lat >= 45.7 && lat <= 45.8 && lng >= 4.8 && lng <= 4.9) {
      return 'Région Lyon';
    } else if (lat >= 43.5 && lat <= 43.7 && lng >= 1.3 && lng <= 1.5) {
      return 'Région Toulouse';
    } else if (lat >= 47.1 && lat <= 47.3 && lng >= -1.6 && lng <= -1.5) {
      return 'Région Nantes';
    } else if (lat >= 41 && lat <= 51 && lng >= -5 && lng <= 10) {
      return 'France';
    }
    
    return `${lat.toFixed(3)}, ${lng.toFixed(3)}`;
  }

  /**
   * Delay helper
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Load request count from localStorage
   */
  private loadRequestCount(): void {
    try {
      const data = localStorage.getItem('geocoding_request_count');
      if (data) {
        const parsed = JSON.parse(data);
        if (parsed.resetTime > Date.now()) {
          this.requestCount = parsed.count || 0;
          this.resetTime = parsed.resetTime;
        }
      }
    } catch (error) {
      console.error('Error loading request count:', error);
    }
  }

  /**
   * Save request count to localStorage
   */
  private saveRequestCount(): void {
    try {
      localStorage.setItem('geocoding_request_count', JSON.stringify({
        count: this.requestCount,
        resetTime: this.resetTime
      }));
    } catch (error) {
      console.error('Error saving request count:', error);
    }
  }

  /**
   * Get service statistics
   */
  getStats(): {
    queueLength: number;
    requestCount: number;
    dailyLimit: number;
    remainingRequests: number;
    resetTime: Date;
    cacheStats: any;
  } {
    return {
      queueLength: this.queue.length,
      requestCount: this.requestCount,
      dailyLimit: this.DAILY_LIMIT,
      remainingRequests: Math.max(0, this.DAILY_LIMIT - this.requestCount),
      resetTime: new Date(this.resetTime),
      cacheStats: GeocodingCache.getStats()
    };
  }

  /**
   * Clear the queue
   */
  clearQueue(): void {
    this.queue.forEach(item => {
      item.resolve('Requête annulée');
    });
    this.queue = [];
  }

  /**
   * Reset daily counter (for testing)
   */
  resetDailyCounter(): void {
    this.requestCount = 0;
    this.resetTime = Date.now() + 86400000;
    this.saveRequestCount();
  }
}